import { useAppSelector } from "@redux/hooks";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import React, { useMemo, useState } from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { useNavigate } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { fetchDataCenterList } from "./requests/request";
import {
  datacenterColumns as baseColumns,
  DataCenterConnection,
  shapeDatacenterData,
} from "./DatacenterData";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import { makeDatacenterFields } from "./datacenterFieldSpec";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

interface MainDataCenter {
  text: string;
}

const NO_DATACENTERS: DataCenterConnection[] = [];

const MainDataCenter: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath.
  const {
    data: datacenterList = NO_DATACENTERS,
    error: fetchError,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(tenantId ? `kontroledge/datacenter-interconnect?tenantId=${tenantId}` : null, async () => {
    const { status, data } = await fetchDataCenterList(api, tenantId);
    if (status >= 500 || data.success === false) {
      throw new Error(data.message || "Unknown server error");
    }
    return Array.isArray(data.data) ? shapeDatacenterData(data.data) : [];
  });
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);


  const dcFields = useMemo(
    () => makeDatacenterFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const normalized = useMemo(
    () => normalizeFilters<DataCenterConnection>(activeFilters, dcFields, {
      defaultStringToContains: true,
      quotedStringsExact: true,
    }),
    [activeFilters, dcFields]
  );

  const predicate = useMemo(
    () => buildPredicate<DataCenterConnection>(normalized, dcFields),
    [normalized, dcFields]
  );

  const filteredData = useMemo(
    () => datacenterList.filter(predicate),
    [datacenterList, predicate]
  );

  const suggestedKeys = useMemo(
    () => dcFields.map(f => f.label),
    [dcFields]
  );
  const columns = useMemo<GenericTableColumn<DataCenterConnection>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: DataCenterConnection) => row.customer?.name ?? "",
        renderCell: (row: DataCenterConnection) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
            </div>
          ) : (
            "—"
          ),
      });
    }
    // CASE A: Partner view (showing ALL customers) → the row's customer, not the lens.
    // CASE B: Customer view (only seeing your own) → the Redux-provided tenantId.
    const datacenterDetailPath = (row: DataCenterConnection) => {
      const idFromRow = Number(row.customer?.id ?? NaN);
      const nextTenantId =
        isViewingAllCustomers && Number.isFinite(idFromRow) ? idFromRow : tenantId;
      return buildDetailPath("datacenter", { datacenterId: row.id }, { tenantId: nextTenantId });
    };

    return cols.map((col) => {
      // 1. Override the “Service ID” column to navigate on click
      if (col.header === "DCI ID") {
        return {
          ...col,
          renderCell: (row: DataCenterConnection) => (
            <button
              {...viewHref(datacenterDetailPath(row))}
              onClick={() => {
                // The tenant rides in the URL, not in history state, so a reload or a
                // pasted link keeps reading the connection in the customer's scope.
                navigate(datacenterDetailPath(row), { state: { datacenterDetail: row } });
              }}
              className="
            hover:text-semantic-text-brand 
            hover:underline
            focus:underline 
            active:underline
            font-medium
            underline
            text-semantic-text-brand
            "
            >
              {row.subscriptionId}
            </button>
          ),
        };
      }
      // 2. Override the “Parent connection” column to navigate on click
      if (col.header === "Parent Connection") {
        return {
          ...col,
          renderCell: (row: DataCenterConnection) => {
            const parent = row.parentConnection;

            if (!parent) return "—";
            return (
              <div className="flex flex-wrap gap-x-2">
                {parent.labelA && parent.idA && (
                  <button
                    {...viewHref(buildDetailPath("port", { portId: parent.idA }, { subid: parent.labelA }))}
                    onClick={() =>
                      navigate(buildDetailPath("port", { portId: parent.idA }, { subid: parent.labelA }))
                    }
                    className="hover:text-semantic-text-brand hover:underline font-medium text-semantic-text-brand underline"
                  >
                    {parent.labelA} ,
                  </button>
                )}
                {parent.labelB &&
                  (() => {
                    // Route the B endpoint by kind: SD-WAN hub -> hub details,
                    // port (or legacy) -> port details, FWAAS -> no per-id
                    // detail route (plain text).
                    let route: string | null = null;
                    if (parent.kindB === "sdwan_hub" && parent.idB) {
                      route = buildDetailPath("sdwanHub", { sdwanId: parent.idB });
                    } else if (
                      (!parent.kindB || parent.kindB === "port") &&
                      parent.idB
                    ) {
                      route = buildDetailPath("port", { portId: parent.idB }, { subid: parent.labelB });
                    }
                    return route ? (
                      <button
                        {...viewHref(route)}
                        onClick={() => navigate(route as string)}
                        className="hover:text-semantic-text-brand hover:underline font-medium text-semantic-text-brand underline"
                      >
                        {parent.labelB}
                      </button>
                    ) : (
                      <span className="font-medium text-semantic-text-primary">
                        {parent.labelB}
                      </span>
                    );
                  })()}
              </div>
            );
          },
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers]);

  const isDemo = isDemoClient();
  const dciColumnVisibility = useColumnVisibility(
    "kontrol:dci:columns",
    columns,
    ["DCI ID"],
  );
  const tableColumns = isDemo ? dciColumnVisibility.visibleColumns : columns;


  //Cases to handle the fetching and the display of empty items
  // If data is being fetched, show only the overlay spinner.
  if (isLoadingList) {
    return <ListPageSkeleton />;
  }
  //If there's an error in the request
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  //If no ports are returned, display the empty state.
  if (datacenterList.length === 0) {
    return <EmptyItems />;
  }
  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <RefreshingIndicator active={isRefreshingList} />

      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">

              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-3 my-5">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder='Find by attribute or value'
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                      liveFiltering
                      debounceMs={200}
                    />
                  </div>
                  {isDemo && (
                    <ColumnVisibilityControl
                      columns={columns}
                      isVisible={dciColumnVisibility.isVisible}
                      toggle={dciColumnVisibility.toggle}
                      requiredIds={["DCI ID"]}
                    />
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={tableColumns}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MainDataCenter;
